import { useState } from "react";
import {
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateScoringArea,
  useCreateScoringSheet,
  useDeleteScoringArea,
  useDeleteScoringSheet,
  useDuplicateScoringSheet,
  useUpdateScoringArea,
  useUpdateScoringSheet,
  type ScoringArea,
  type ScoringSheet,
} from "../../../lib/queries/rules";
import { formatPoints, parsePointsInput } from "../../../lib/rulesLabels";
import { ComponentCard } from "./ComponentCard";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "sheet"; sheet?: ScoringSheet }
  | { kind: "row"; sheetId: ID; row?: ScoringArea };

interface SheetForm {
  name: string;
  description: string;
}

interface RowForm {
  name: string;
  description: string;
  max_points: string;
  step: string;
}

const emptySheetForm: SheetForm = { name: "", description: "" };
const emptyRowForm: RowForm = {
  name: "",
  description: "",
  max_points: "10,00",
  step: "0,10",
};

/** Sums the area maxima so a sheet shows what a perfect score is worth. */
function sheetTotalCp(sheet: ScoringSheet): number {
  return (sheet.areas ?? []).reduce((total, area) => total + area.max_points_cp, 0);
}

export function ScoringSheetsTab({
  ruleSetId,
  readOnly,
  sheets,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  sheets: ScoringSheet[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [sheetForm, setSheetForm] = useState<SheetForm>(emptySheetForm);
  const [rowForm, setRowForm] = useState<RowForm>(emptyRowForm);

  const createSheet = useCreateScoringSheet(ruleSetId);
  const updateSheet = useUpdateScoringSheet();
  const deleteSheet = useDeleteScoringSheet();
  const createRow = useCreateScoringArea();
  const updateRow = useUpdateScoringArea();
  const deleteRow = useDeleteScoringArea();
  const duplicateSheet = useDuplicateScoringSheet();

  const close = () => {
    setModal({ kind: "closed" });
    createSheet.reset();
    updateSheet.reset();
    duplicateSheet.reset();
    createRow.reset();
    updateRow.reset();
  };

  const openSheet = (sheet?: ScoringSheet) => {
    setSheetForm(
      sheet ? { name: sheet.name, description: sheet.description } : emptySheetForm,
    );
    createSheet.reset();
    updateSheet.reset();
    setModal({ kind: "sheet", sheet });
  };

  const openRow = (sheetId: ID, row?: ScoringArea) => {
    setRowForm(
      row
        ? {
            name: row.name,
            description: row.description,
            max_points: formatPoints(row.max_points_cp),
            step: formatPoints(row.step_cp),
          }
        : emptyRowForm,
    );
    createRow.reset();
    updateRow.reset();
    setModal({ kind: "row", sheetId, row });
  };

  const submitSheet = () => {
    if (modal.kind !== "sheet") return;
    const payload = {
      name: sheetForm.name.trim(),
      description: sheetForm.description.trim(),
    };
    if (modal.sheet) {
      updateSheet.mutate({ id: modal.sheet.id, payload }, { onSuccess: close });
    } else {
      createSheet.mutate(payload, { onSuccess: close });
    }
  };

  const submitRow = () => {
    if (modal.kind !== "row") return;
    const maxPointsCp = parsePointsInput(rowForm.max_points);
    const stepCp = parsePointsInput(rowForm.step);
    if (maxPointsCp === null || stepCp === null) return;
    const payload = {
      name: rowForm.name.trim(),
      description: rowForm.description.trim(),
      max_points_cp: maxPointsCp,
      step_cp: stepCp,
      sort_order:
        modal.row?.sort_order ??
        (sheets.find((s) => s.id === modal.sheetId)?.areas ?? []).length,
    };
    if (modal.row) {
      updateRow.mutate({ id: modal.row.id, payload }, { onSuccess: close });
    } else {
      createRow.mutate({ sheetId: modal.sheetId, payload }, { onSuccess: close });
    }
  };

  const move = (sheet: ScoringSheet, index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(sheet.areas ?? [], index, direction)) {
      updateRow.mutate({
        id: row.id,
        payload: {
          name: row.name,
          description: row.description,
          max_points_cp: row.max_points_cp,
          step_cp: row.step_cp,
          sort_order,
        },
      }, { onError: (err) => notifyError(err) });
    }
  };

  const maxPointsCp = parsePointsInput(rowForm.max_points);
  const stepCp = parsePointsInput(rowForm.step);
  // A step that does not divide the ceiling makes the top score
  // unreachable; the backend refuses it, so say so before the round trip.
  const stepMismatch =
    maxPointsCp !== null && stepCp !== null && stepCp > 0 && maxPointsCp % stepCp !== 0;
  const rowInvalid =
    !rowForm.name.trim() || maxPointsCp === null || stepCp === null || stepMismatch;

  return (
    <>
      <Page.SectionHeader
        title="Hodnoticí tabulky"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => openSheet()}>
              Nová tabulka
            </Button>
          )
        }
      />
      <Text variant="muted">
        ČNSMS hodnoticí tabulky nezveřejňuje a stupnice se liší podle disciplíny, proto si
        je zakládáte sami.
      </Text>

      {sheets.length === 0 ? (
        <EmptyState
          title="Žádné hodnoticí tabulky"
          description="Založte tabulku a rozepište oblasti, které porotce hodnotí."
        />
      ) : (
        sheets.map((sheet) => (
          <ComponentCard<ScoringArea>
            key={sheet.id}
            title={sheet.name}
            subtitle={
              <Text variant="muted">celkem {formatPoints(sheetTotalCp(sheet))} b</Text>
            }
            readOnly={readOnly}
            rows={sheet.areas ?? []}
            emptyTitle="Zatím bez oblastí"
            emptyDescription="Přidejte první hodnoticí oblast."
            addLabel="Přidat oblast"
            columns={[
              {
                key: "name",
                header: "Oblast",
                tdClass: tableStyles.nameOnly,
                render: (r) => (
                  <>
                    <div>{r.name}</div>
                    {r.description && <Text variant="muted">{r.description}</Text>}
                  </>
                ),
              },
              {
                key: "max",
                header: "Maximum",
                numeric: true,
                tdClass: tableStyles.value,
                render: (r) => <Text variant="mono">{formatPoints(r.max_points_cp)} b</Text>,
              },
              {
                key: "step",
                header: "Krok",
                numeric: true,
                tdClass: tableStyles.value,
                render: (r) => <Text variant="mono">{formatPoints(r.step_cp)}</Text>,
              },
            ]}
            onEditTable={() => openSheet(sheet)}
            onDuplicateTable={() => duplicateSheet.mutate({ id: sheet.id }, { onError: (err) => notifyError(err) })}
            onDeleteTable={() =>
              confirm({
                title: "Smazat hodnoticí tabulku",
                description: (
                  <>
                    Smaže se tabulka <strong>{sheet.name}</strong> i všechny její oblasti.
                    Tabulku, na kterou odkazuje disciplína nebo některá kombinace, smazat nelze.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteSheet.mutateAsync(sheet.id),
              })
            }
            onAddRow={() => openRow(sheet.id)}
            onEditRow={(row) => openRow(sheet.id, row)}
            onDeleteRow={(row) =>
              confirm({
                title: "Smazat hodnoticí oblast",
                description: (
                  <>
                    Smaže se oblast <strong>{row.name}</strong>.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteRow.mutateAsync(row.id),
              })
            }
            onMoveRow={(index, direction) => move(sheet, index, direction)}
          />
        ))
      )}

      <FormModal
        open={modal.kind === "sheet"}
        title={modal.kind === "sheet" && modal.sheet ? "Upravit tabulku" : "Nová hodnoticí tabulka"}
        onClose={close}
        submitLabel="Uložit"
        busy={createSheet.isPending || updateSheet.isPending}
        canSubmit={!!sheetForm.name.trim()}
        error={createSheet.error || updateSheet.error}
        onSubmit={submitSheet}
      >
        <Input
          label="Název tabulky"
          required
          placeholder="Hodnocení MD Dance"
          value={sheetForm.name}
          onChange={(e) => setSheetForm({ ...sheetForm, name: e.target.value })}
        />
        <Textarea
          label="Popis"
          rows={2}
          value={sheetForm.description}
          onChange={(e) => setSheetForm({ ...sheetForm, description: e.target.value })}
        />
      </FormModal>

      <FormModal
        open={modal.kind === "row"}
        title={modal.kind === "row" && modal.row ? "Upravit oblast" : "Nová hodnoticí oblast"}
        onClose={close}
        submitLabel="Uložit"
        busy={createRow.isPending || updateRow.isPending}
        canSubmit={!rowInvalid}
        error={createRow.error || updateRow.error}
        onSubmit={submitRow}
      >
        <Input
          label="Název oblasti"
          required
          placeholder="Technika"
          value={rowForm.name}
          onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })}
        />
        <ResponsiveGrid columns={2}>
          <Input
            label="Maximum (body)"
            required
            placeholder="10,00"
            value={rowForm.max_points}
            onChange={(e) => setRowForm({ ...rowForm, max_points: e.target.value })}
          />
          <Input
            label="Krok hodnocení"
            required
            placeholder="0,10"
            value={rowForm.step}
            onChange={(e) => setRowForm({ ...rowForm, step: e.target.value })}
            error={stepMismatch ? "Krok musí dělit maximum beze zbytku." : undefined}
          />
        </ResponsiveGrid>
        <Textarea
          label="Popis"
          rows={2}
          value={rowForm.description}
          onChange={(e) => setRowForm({ ...rowForm, description: e.target.value })}
          hint="Nepovinné vodítko pro porotce."
        />
      </FormModal>


    </>
  );
}
